// The job's visits: the list, fix a visit, confirm for them.
import { addDays, type JobDetail, type VisitCard } from "@steward/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useClient, usePeople, useUpdateJob, useVisitFix, useVisits } from "../../api/hooks";
import { useSession } from "../../app/session";
import { FormField, InputText } from "../../components/fields";
import { clock, daysAway, dayWords, money, shortDate } from "../../components/format";
import { Modal, useToast } from "../../components/overlays";
import { Banner, Button, Card, Skeleton } from "../../components/parts";
import { CrewChips, errText, VisitStatus } from "../../components/work";
import { PhoneCard } from "../PhoneCard";

export function JobVisits({ job, today }: { job: JobDetail; today: string }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const visits = useVisits(
    company.id,
    { from: addDays(today, -60), to: addDays(today, 90), jobId: job.id },
    can("schedule.read"),
  );
  if (!can("schedule.read")) return null;
  const [fixing, setFixing] = useState(false);
  const repeating = job.frequency !== "one-time";
  const all = visits.data?.visits ?? [];
  // A complaint about a done one-time job: the crew goes back for free (api visits/fix.ts).
  const fixFor = repeating ? undefined : all.filter((v) => v.status === "done" && !v.fix).at(-1);
  const upcoming = all.filter((v) => v.date >= today && v.status !== "done").slice(0, 6);
  const recent = all
    .filter((v) => v.date < today || v.status === "done")
    .reverse()
    .slice(0, 4);
  const row = (v: (typeof all)[number]) => (
    <button
      type="button"
      className="item visit-row"
      key={v.ref}
      onClick={() => void navigate({ to: "/visits/$ref", params: { ref: v.ref } })}
    >
      <span className="when">
        <b>{dayWords(v.date, today)}</b>
        <span className="muted num">{clock(v.startTime)}</span>
      </span>
      <div className="what">
        <b>{daysAway(v.date, today)}</b>
        <span>{v.fix ? "Fix visit, no charge" : v.moved ? `Moved from ${shortDate(v.originalDate)}` : money(v.totalCents)}</span>
      </div>
      <CrewChips crew={v.crew} />
      <VisitStatus status={v.status} late={v.date < today} />
    </button>
  );
  return (
    <Card
      title={repeating ? "Scheduled visits" : "Visits"}
      flush
      action={
        repeating && can("job.update") ? (
          <Button type="secondary" size="small" onClick={() => void navigate({ to: "/jobs/$id/edit", params: { id: job.id } })}>
            Edit all visits
          </Button>
        ) : fixFor && can("schedule.update") ? (
          <Button type="secondary" size="small" onClick={() => setFixing(true)}>
            Add fix visit
          </Button>
        ) : undefined
      }
    >
      {fixing && fixFor ? (
        <FixVisit
          job={job}
          fixFor={fixFor}
          today={today}
          onClose={() => setFixing(false)}
          onDone={(ref) => void navigate({ to: "/visits/$ref", params: { ref } })}
        />
      ) : null}
      {repeating ? (
        <dl className="visit-sum">
          <div>
            <dt>First visit</dt>
            <dd>{job.startDate ? shortDate(job.startDate) : "Not set"}</dd>
          </div>
          <div>
            <dt>Last visit</dt>
            <dd>{job.endsOn ? shortDate(job.endsOn) : "Keeps going"}</dd>
          </div>
          <div>
            <dt>Repeats</dt>
            <dd>{job.frequencyLabel}</dd>
          </div>
          <div>
            <dt>Checklist</dt>
            <dd>{job.checklist?.name ?? "None"}</dd>
          </div>
        </dl>
      ) : null}
      {visits.isLoading ? (
        <Skeleton rows={3} />
      ) : (
        <>
          <h3 className="card-sub">Coming up</h3>
          <div className="todo">
            {upcoming.length ? upcoming.map(row) : <div className="card-empty muted">Nothing planned.</div>}
          </div>
          <h3 className="card-sub">Recent</h3>
          <div className="todo">{recent.length ? recent.map(row) : <div className="card-empty muted">No visits yet.</div>}</div>
        </>
      )}
    </Card>
  );
}

/**
 * Add fix visit (owner, Oct 31): the client complained about a done one-time job, so the crew goes back. It is a visit of
 * the same job with no charge; it goes on the same invoice when done, and no automatic text goes out for it.
 */
function FixVisit({
  job,
  fixFor,
  today,
  onClose,
  onDone,
}: {
  job: JobDetail;
  fixFor: VisitCard;
  today: string;
  onClose: () => void;
  onDone: (ref: string) => void;
}) {
  const { company } = useSession();
  const toast = useToast();
  const people = usePeople(company.id);
  const fix = useVisitFix();
  const team = (people.data ?? []).filter((p) => p.active);
  const [date, setDate] = useState(addDays(today, 1));
  const [time, setTime] = useState(fixFor.startTime ?? job.startTime ?? "09:00");
  const [crew, setCrew] = useState<Set<string>>(new Set(fixFor.crew.map((c) => c.id)));
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const save = () =>
    fix.mutate(
      { ref: fixFor.ref, date, startTime: time || null, crew: [...crew], crewNotes: note.trim() || null },
      {
        onSuccess: (d) => {
          toast(`Fix visit added for ${shortDate(d.date)}`);
          onClose();
          onDone(d.ref);
        },
        onError: (e) => setError(errText(e)),
      },
    );
  return (
    <Modal
      title="Add fix visit"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!date} loading={fix.isPending} onClick={save}>
            Add the visit
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">
          For a complaint about the {shortDate(fixFor.date)} visit. There is no charge. It goes on the same invoice when it is
          done, and the client gets no automatic text.
        </p>
        {error ? <Banner type="error">{error}</Banner> : null}
        <div className="row">
          <InputText label="Day" type="date" min={fixFor.date} value={date} onChange={(e) => setDate(e.target.value)} />
          <InputText label="Start time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>
        <FormField label="Who goes">
          <div className="pick-list">
            {team.map((p) => (
              <label key={p.id} className="pick">
                <input
                  type="checkbox"
                  checked={crew.has(p.id)}
                  onChange={() => {
                    const n = new Set(crew);
                    if (n.has(p.id)) n.delete(p.id);
                    else n.add(p.id);
                    setCrew(n);
                  }}
                />
                <b>{p.name}</b>
              </label>
            ))}
          </div>
        </FormField>
        <InputText
          label="What to fix"
          description="The crew sees this on the visit."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  );
}

// Form ---------------------------------------------------------------------------------------------------------------

/**
 * Confirm for them (owner, Oct 31): a client who cannot use the link (older, no smartphone) confirms on the phone. The office
 * takes their card by phone if none is on file, then confirms. The job keeps who confirmed and when (confirmedByHand).
 */
export function ConfirmForThem({ job, onClose }: { job: JobDetail; onClose: () => void }) {
  const toast = useToast();
  const update = useUpdateJob();
  const client = useClient(job.clientId);
  const [taking, setTaking] = useState(false);
  const c = client.data;
  if (taking && c) return <PhoneCard c={c} onClose={() => setTaking(false)} onSaved={() => setTaking(false)} />;
  const confirm = () =>
    update.mutate(
      { id: job.id, patch: { active: true } },
      {
        onSuccess: () => {
          toast(`Confirmed for ${job.clientName}`);
          onClose();
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <Modal
      title={`Confirm for ${job.clientName}`}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={update.isPending} disabled={!c} onClick={confirm}>
            Confirm the job
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">
          Use this when they said yes on the phone and cannot sign the link. No signature is needed. We save your name and the
          time as the OK.
        </p>
        {!c ? (
          <Skeleton rows={2} />
        ) : c.card ? (
          <Banner type="success">
            Card on file, {c.card.brand} ending in {c.card.last4}.
          </Banner>
        ) : (
          <Banner
            type="warning"
            action={
              <Button size="small" type="secondary" onClick={() => setTaking(true)}>
                Take card by phone
              </Button>
            }
          >
            No card on file yet.
          </Banner>
        )}
      </div>
    </Modal>
  );
}
